import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

/**
 * Rango permitido para el escalado de UI
 */
const MIN_SCALE = 0.5; // 50%
const MAX_SCALE = 1.25; // 125%
const DEFAULT_SCALE = 1.0; // 100%
const STORAGE_KEY = 'wayline-ui-scale';

/**
 * Tipo del contexto de escalado UI
 */
interface UIScaleContextType {
  scale: number;
  setScale: (scale: number) => void;
  resetScale: () => void;
  increaseScale: () => void;
  decreaseScale: () => void;
}

const UIScaleContext = createContext<UIScaleContextType | undefined>(undefined);

/**
 * UIScaleProvider - Proveedor de contexto para escalado global de UI
 *
 * @remarks
 * Gestiona el factor de escala global de la UI de WESTEER.
 * - Persistencia en localStorage
 * - Aplica la escala mediante CSS variable --ui-scale
 * - Rango permitido: 0.5 - 1.25 (50% - 125%)
 *
 * @example
 * ```tsx
 * // En App.tsx
 * import { UIScaleProvider } from '@/core/ui-scale';
 *
 * <UIScaleProvider>
 *   <App />
 * </UIScaleProvider>
 * ```
 */
export function UIScaleProvider({ children }: { children: ReactNode }): JSX.Element {
  // Cargar escala desde localStorage o usar default
  const [scale, setScaleState] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = parseFloat(stored);
        if (parsed >= MIN_SCALE && parsed <= MAX_SCALE) {
          return parsed;
        }
      }
    } catch (error) {
      console.error('Error loading UI scale from localStorage:', error);
    }
    return DEFAULT_SCALE;
  });

  /**
   * Actualiza la escala y persiste en localStorage
   */
  const setScale = (newScale: number): void => {
    const clamped = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
    setScaleState(clamped);

    try {
      localStorage.setItem(STORAGE_KEY, clamped.toString());
    } catch (error) {
      console.error('Error saving UI scale to localStorage:', error);
    }
  };

  /**
   * Resetea la escala al valor por defecto
   */
  const resetScale = (): void => {
    setScale(DEFAULT_SCALE);
  };

  /**
   * Incrementa la escala en 0.05 (5%)
   */
  const increaseScale = (): void => {
    setScale(scale + 0.05);
  };

  /**
   * Decrementa la escala en 0.05 (5%)
   */
  const decreaseScale = (): void => {
    setScale(scale - 0.05);
  };

  /**
   * Aplica la escala al CSS custom property --ui-scale
   */
  useEffect(() => {
    document.documentElement.style.setProperty('--ui-scale', scale.toString());
  }, [scale]);

  return (
    <UIScaleContext.Provider
      value={{
        scale,
        setScale,
        resetScale,
        increaseScale,
        decreaseScale,
      }}
    >
      {children}
    </UIScaleContext.Provider>
  );
}

/**
 * Hook para acceder al contexto de escalado UI
 *
 * @throws Error si se usa fuera del UIScaleProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { scale, setScale, increaseScale } = useUIScale();
 *
 *   return (
 *     <div>
 *       <p>Escala actual: {Math.round(scale * 100)}%</p>
 *       <button onClick={increaseScale}>Aumentar</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useUIScale(): UIScaleContextType {
  const context = useContext(UIScaleContext);

  if (!context) {
    throw new Error('useUIScale debe usarse dentro de un UIScaleProvider');
  }

  return context;
}

/**
 * Constantes exportadas para uso externo
 */
export const UI_SCALE_CONFIG = {
  MIN: MIN_SCALE,
  MAX: MAX_SCALE,
  DEFAULT: DEFAULT_SCALE,
  STEP: 0.05,
} as const;
